import React, { useRef, useEffect, useState, useCallback } from 'react';
import { IconButton, Stack, IIconProps, IButtonStyles, Text, getTheme } from '@fluentui/react';
import { Frame, QueueUxPreferences, SubjectImagePreferences } from 'services';
import Slider from 'rc-slider';

import 'rc-slider/assets/index.css';
import './VideoContent.scss';
import { generateImageFilters } from '../../util/smallFuncs';
import { DEFAULT_MAX_MEDIA_HEIGHT } from '../../admin/MediaHeightField';
import { WhiteStatic } from '../../../theme';

const speedOptionsHolderStyle: React.CSSProperties = {
  bottom: '32px',
  backgroundColor: 'rgba(16, 17, 18, 0.8)',
  borderRadius: '4px'
};

const speedOptionStyle: React.CSSProperties = {
  cursor: 'pointer',
  color: WhiteStatic
};

const playIIcon: IIconProps = { iconName: 'Play' };
const pausedIcon: IIconProps = { iconName: 'Pause' };
const fastForwardIcon: IIconProps = { iconName: 'FastForward' };
const rewindIcon: IIconProps = { iconName: 'Rewind' };
const volumeUpIcon: IIconProps = { iconName: 'Volume3' };
const volumeDisabledIcon: IIconProps = { iconName: 'VolumeDisabled' };
const fullscreenIcon: IIconProps = { iconName: 'FullScreen' };

export interface VideoContentProps {
  src: string;
  uxPreferences?: QueueUxPreferences;
  subjectImagePreferences?: SubjectImagePreferences;
  frames?: Frame[];
  className?: string;
  onClick?: () => void;
  isZoomInActive?: boolean;
  isGridView?: boolean;
}

export const VideoContent: React.FunctionComponent<VideoContentProps> = props => {
  const videoWrapperStyle = props.isGridView
    ? `h-100 w-100 ${props.className || ''}`
    : `w-100 ${props.className || ''}`;
  const frameMarker =
    props.frames &&
    props.frames.reduce((prev, cur) => {
      return { ...prev, [cur.time]: undefined };
    }, {});
  const [isPaused, setPaused] = useState<boolean>(true);
  const [isMuted, setMuted] = useState<boolean>(false);
  const [showSpeedDropdown, setSpeedDropdown] = useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const { palette } = getTheme();

  const controlsStyle = (isZoomInActive): React.CSSProperties => {
    const controlsStyle: React.CSSProperties = {
      zIndex: 1,
      bottom: 0,
      background: 'linear-gradient(-180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.9) 100%)',
      color: palette.white,
      pointerEvents: 'all',
      borderRadius: isZoomInActive ? '0px 0px 4px 4px' : 0
    };
    return controlsStyle;
  };

  const controlButtonStyle: Partial<IButtonStyles> = {
    icon: { color: WhiteStatic, fontSize: 16 },
    root: { margin: 0, padding: 0, borderRadius: 4 },
    rootHovered: { background: 'rgb(255, 255, 255, 0.2)' },
    rootPressed: { background: 'rgb(255, 255, 255, 0.3)' }
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const togglePaused = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    if (isPaused) videoRef.current && videoRef.current.play();
    else videoRef.current && videoRef.current.pause();
    setPaused(!isPaused);
  };
  const goToNextFrame = useCallback(() => {
    if (!videoRef.current || !props.frames) {
      return;
    }
    const currentTime = videoRef.current.currentTime;

    // The issue here is that the frame time is accurate to this many decimals: 134.20056666666667
    // and the currentTime is only accurate to this many: 134.200566 So when checking for strictly greater than we
    // will see the current frame appear to be the 'next frame' resulting in clicking next taking us to the current frame
    // (doing nothing). So we're adding in a tiny 'buffer' to make sure the next frame is at least one hundredth of a millisecond
    // ahead of the current frame.
    const next = props.frames.find(item => item.time > currentTime + 0.00001);

    if (next) {
      videoRef.current.currentTime = next.time;
    }
  }, [props.frames, videoRef]);
  const goToPreviousFrame = useCallback(() => {
    if (!videoRef.current || !props.frames) {
      return;
    }
    if (isPaused) {
      videoRef.current && videoRef.current.pause();
      setPaused(true);
    }
    const currentTime = videoRef.current.currentTime;
    for (let i = props.frames.length; i-- > 0; ) {
      if (props.frames[i].time < currentTime) {
        videoRef.current.currentTime = props.frames[i].time;
        return;
      }
    }
  }, [props.frames, videoRef]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!videoRef.current) {
        return;
      }

      setCurrentTime(videoRef.current.currentTime);
    }, 100);
    return (): void => clearInterval(interval);
  }, []);

  const imageFilter = generateImageFilters(props.subjectImagePreferences);

  return (
    <Stack
      horizontalAlign="center"
      verticalAlign="center"
      className={videoWrapperStyle}
      style={{ maxHeight: `${props.subjectImagePreferences?.maxHeight || DEFAULT_MAX_MEDIA_HEIGHT}px` }}
    >
      <Stack.Item
        className={'w-100 h-100 overflow-hidden position-relative ' + (props.isZoomInActive ? 'rounded' : '')}
      >
        <div className="position-absolute w-100 h-75" onClick={props.onClick} style={{ pointerEvents: 'all' }}></div>
        <Stack className="position-absolute w-100" style={controlsStyle(props.isZoomInActive)} horizontal>
          {props.frames && (
            <Stack.Item>
              <IconButton
                iconProps={rewindIcon}
                title="Back"
                ariaLabel="Back"
                onClick={goToPreviousFrame}
                styles={controlButtonStyle}
              />
            </Stack.Item>
          )}
          <Stack.Item>
            <IconButton
              iconProps={!isPaused ? pausedIcon : playIIcon}
              title="Play"
              ariaLabel="Play"
              onClick={togglePaused}
              styles={controlButtonStyle}
            />
          </Stack.Item>
          {props.frames && (
            <Stack.Item>
              <IconButton
                iconProps={fastForwardIcon}
                title="Next"
                ariaLabel="Next"
                onClick={goToNextFrame}
                styles={controlButtonStyle}
              />
            </Stack.Item>
          )}
          <Stack.Item grow align="center" className="px-2 h-100">
            <Slider
              min={0}
              ariaLabelForHandle="video slider"
              max={Math.floor((videoRef.current && videoRef.current.duration) || 0)}
              marks={frameMarker}
              defaultValue={0}
              step={0.01}
              value={currentTime}
              onChange={(value: number): void => {
                if (videoRef.current) {
                  videoRef.current.currentTime = value;
                  setCurrentTime(value);
                }
              }}
            />
          </Stack.Item>
          {videoRef.current && videoRef.current.offsetWidth > 400 && (
            <Stack className="position-relative playback-picker">
              <Stack.Item className="py-1 px-2">
                <Text
                  variant="mediumPlus"
                  style={speedOptionStyle}
                  onClick={() => setSpeedDropdown(!showSpeedDropdown)}
                >
                  {playbackSpeed}x
                </Text>
              </Stack.Item>
              {showSpeedDropdown && (
                <div className="position-absolute" style={speedOptionsHolderStyle}>
                  <Stack.Item className="playback-picker py-1 px-2">
                    <Text
                      variant="mediumPlus"
                      style={speedOptionStyle}
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.playbackRate = 2;
                          setPlaybackSpeed(2);
                          setSpeedDropdown(!showSpeedDropdown);
                        }
                      }}
                    >
                      2x
                    </Text>
                  </Stack.Item>
                  <Stack.Item className="playback-picker py-1 px-2">
                    <Text
                      variant="mediumPlus"
                      style={speedOptionStyle}
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.playbackRate = 1.5;
                          setPlaybackSpeed(1.5);
                          setSpeedDropdown(!showSpeedDropdown);
                        }
                      }}
                    >
                      1.5x
                    </Text>
                  </Stack.Item>
                  <Stack.Item className="playback-picker py-1 px-2">
                    <Text
                      variant="mediumPlus"
                      style={speedOptionStyle}
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.playbackRate = 1;
                          setPlaybackSpeed(1);
                          setSpeedDropdown(!showSpeedDropdown);
                        }
                      }}
                    >
                      1x
                    </Text>
                  </Stack.Item>
                </div>
              )}
            </Stack>
          )}

          {videoRef.current && videoRef.current.offsetWidth > 400 && (
            <Stack.Item>
              <IconButton
                iconProps={isMuted ? volumeDisabledIcon : volumeUpIcon}
                title="Sound"
                ariaLabel="Sound"
                onClick={() => setMuted(!isMuted)}
                styles={controlButtonStyle}
              />
            </Stack.Item>
          )}
          {videoRef.current && videoRef.current.offsetWidth > 400 && (
            <Stack.Item>
              <IconButton
                iconProps={fullscreenIcon}
                title="Fullscreen"
                ariaLabel="Fullscreen"
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  videoRef.current && videoRef.current.requestFullscreen();
                }}
                styles={controlButtonStyle}
              />
            </Stack.Item>
          )}
        </Stack>
        <video
          className="h-100 w-100 d-block mx-auto"
          ref={videoRef}
          muted={isMuted}
          onEnded={() => setPaused(true)}
          style={{ ...imageFilter }}
        >
          <source src={props.src} />
          Your browser does not support the video tag.
        </video>
      </Stack.Item>
    </Stack>
  );
};
